/* FLYER — everything the leaflet of a point is built out of. Two sides of one sheet: the face
   carries the offer over a photo of the place, the back tells how the discount is taken and holds
   the code. The drawing itself lives in FlyerSheet.js and knows nothing about the form */

/* Millimetres of a sheet become the pixels of the drawing at 96 dots per inch */
export const MM = 96 / 25.4

/* The picture is taken three times bigger than the screen — that is the print */
export const PRINT_RATIO = 300 / 96

/* Both sides are laid out at this size. Every A sheet keeps the same proportion, so one drawing
   prints on all three of them */
export const CANVAS = { width: 840, height: 1188 }

/* The sheets a leaflet is printed on */
export const FORMATS = [
    { key: "A5", text: "A5", hint: "148 × 210 мм", width: 148, height: 210 },
    { key: "A6", text: "A6", hint: "105 × 148 мм", width: 105, height: 148 },
    { key: "A4", text: "A4", hint: "210 × 297 мм", width: 210, height: 297 }
]

/* The sides. They are drawn together and downloaded either together or one at a time */
export const SIDES = [
    { key: "front", text: "Лицо" },
    { key: "back", text: "Оборот" }
]

/* What stands across the very top of both sides */
export const RULES = [
    { key: "line", text: "Линия" },
    { key: "stripes", text: "Полосы" },
    { key: "none", text: "Ничего" }
]

/* The photo of the place is either darkened under the letters or washed out under them */
export const VEILS = [
    { key: "dark", text: "Затемнить" },
    { key: "light", text: "Осветлить" },
    { key: "none", text: "Без фото" }
]

/* The letters of the headings */
export const TYPES = [
    { key: "serif", text: "С засечками" },
    { key: "sans", text: "Без засечек" }
]

/* What stands in the corner of the leaflet — the mark of the point, its name, or both */
export const BRANDS = [
    { key: "both", text: "Логотип с названием" },
    { key: "logo", text: "Только логотип" },
    { key: "text", text: "Только название" }
]

/* The card with the code on the back */
export const CARDS = [
    { key: "tint", text: "Светлая" },
    { key: "dark", text: "Тёмная" }
]

/* The ready looks. Every one of them only fills the form — after that every knob turns by hand */
export const THEMES = [
    {
        key: "gold",
        text: "Золото",
        accent: "#C6A15B",
        band: "#141414",
        veil: "dark",
        type: "serif",
        rule: "line",
        card: "tint"
    },
    {
        key: "rose",
        text: "Роза",
        accent: "#A2626C",
        band: "#FFFFFF",
        veil: "light",
        type: "serif",
        rule: "line",
        card: "tint"
    },
    {
        key: "blue",
        text: "Синий",
        accent: "#2F6BE0",
        band: "#FFFFFF",
        veil: "dark",
        type: "sans",
        rule: "stripes",
        card: "dark"
    },
    {
        key: "graphite",
        text: "Графит",
        accent: "#2B2B2B",
        band: "#FFFFFF",
        veil: "dark",
        type: "sans",
        rule: "line",
        card: "dark"
    }
]

/* The empty form of a leaflet. The lines with the braces in them take the name of the point and
   the size of the discount at the moment they are drawn, so one set of texts fits every point */
export const INITIAL = {

    theme: "gold",
    format: "A5",

    accent: "#C6A15B",
    band: "#141414",
    veil: "dark",
    type: "serif",
    rule: "line",
    card: "tint",

    brand: "both",
    discount: "20",

    badge: "СПЕЦИАЛЬНОЕ ПРЕДЛОЖЕНИЕ",
    offer: "на все услуги барбершопа",
    lead: "Запишись в {name} через {site} — и получи скидку на посещение.",
    strip: "Онлайн-запись в барбершоп",

    how: "Как получить скидку",
    first: "Отсканируй QR-код и перейди на сайт bookify",
    second: "Найди {name} и выбери услугу",
    third: "Забронируй время — скидка {discount}% применится автоматически",

    scanTitle: "Сканируй и записывайся",
    scanText: "Наведи камеру телефона на QR-код, чтобы открыть сайт bookify",

    tagline: "Мужские стрижки · борода · бритьё",
    fine: "Акция действует при онлайн-записи через сервис bookify. Скидка не суммируется с другими предложениями.",

    withAddress: true,
    withTagline: true,
    withFine: true,
    name: "",
    address: "",

    link: ""
}


/* The pin of the address — the icon lives here and not in the set of the panel, because it is
   printed and not clicked */
export const pinIcon = () => {
    return (
        <svg viewBox="0 0 24 24" fill="none" className="flyer-pin">
            <path d="M12 21.6s7.2-6.16 7.2-11.6A7.2 7.2 0 0 0 4.8 10c0 5.44 7.2 11.6 7.2 11.6Z" fill="currentColor" />
            <circle cx="12" cy="9.8" r="2.6" fill="#fff" />
        </svg>
    )
}


/* A line of the form with the braces in it. The name of the point and the address of the site are
   drawn heavier than the words around them — that is the whole trick of the lead */
export const fill = (template, values) => {

    const parts = `${template}`.split(/(\{[a-zA-Z]+\})/g)

    return parts.map((part, index) => {

        const match = part.match(/^\{([a-zA-Z]+)\}$/)

        if (!match) {
            return part
        }

        const value = values[`${match[1]}`]

        if (value === undefined || value === null) {
            return part
        }

        return <b key={`${index}`}>{value}</b>
    })
}


/* One colour as a number of every channel */
const channels = color => {

    const clean = `${color}`.replace("#", "").trim()

    if (clean.length === 3) {
        return [parseInt(clean[0] + clean[0], 16), parseInt(clean[1] + clean[1], 16), parseInt(clean[2] + clean[2], 16)]
    }

    if (clean.length === 6) {
        return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)]
    }

    return [0, 0, 0]
}


/* The colour of the letters the eye reads on such a background */
export const inkOf = color => {

    const [red, green, blue] = channels(color)
    const brightness = (red * 299 + green * 587 + blue * 114) / 1000

    if (brightness > 150) {
        return "dark"
    }

    return "light"
}


/* The same colour, only thinner — it stands under the numbers of the steps and under the card
   with the code, where the accent itself would shout */
export const fadeOf = (color, alpha) => {

    const [red, green, blue] = channels(color)

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}


/* The link the camera opens */
export const linkOf = (organization, custom = "") => {

    const clean = `${custom}`.trim()

    if (clean.length > 0) {
        return clean
    }

    if (!organization) {
        return "https://bookify.kz"
    }

    const token = organization.url ? organization.url : organization.uid

    if (!token) {
        return "https://bookify.kz"
    }

    return `https://i.bookify.kz/g/${token}?source=qr`
}


/* The name of the file which lands in the downloads */
export const fileOf = (organization, side, extension) => {

    let token = "bookify"

    if (organization && organization.url) {
        token = `${organization.url}`
    }
    else if (organization && organization.name) {
        token = `${organization.name}`.toLowerCase().replace(/[^a-zа-яё0-9]+/gi, "-").replace(/^-|-$/g, "")
    }

    if (!side) {
        return `${token}-reflet.${extension}`
    }

    return `${token}-reflet-${side}.${extension}`
}


/* How thick the print comes out. The drawing is one for the whole A row, so the smaller the sheet
   the denser the same picture lies on it — the number is counted and not promised */
export const dpiOf = (pixels, millimetres) => {

    if (!millimetres) {
        return 0
    }

    return Math.round(pixels / (millimetres / 25.4))
}


/* A picture from another address does not get into the canvas — the browser closes it for the
   safety of the page. So the file is read first and only its own contents go into the drawing */
export const dataOf = url => {

    return fetch(url).then(response => {

        if (!response.ok) {
            throw new Error("failed")
        }

        return response.blob()
    }).then(blob => new Promise((resolve, reject) => {

        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(new Error("failed"))
        reader.readAsDataURL(blob)
    }))
}


/* A file chosen in the form becomes the contents of the picture the same way */
export const readOf = file => {

    return new Promise((resolve, reject) => {

        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(new Error("failed"))
        reader.readAsDataURL(file)
    })
}


/* A long offer does not fit the sheet in the biggest letters */
export const offerSize = offer => {

    const length = `${offer}`.trim().length

    if (length > 34) {
        return 32
    }

    if (length > 24) {
        return 38
    }

    return 44
}
